import { useState, useMemo } from 'react';
import type { Lead, Turma, LeadOrigem } from '@/mocks/admin';
import LeadDetalheModal from './LeadDetalheModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import GerarDiagnosticoModal from './GerarDiagnosticoModal';
import GerarPropostaModal from './GerarPropostaModal';
import CustomSelect from './CustomSelect';

interface AdminLeadsProps {
  leads: Lead[];
  turmas: Turma[];
  onStatusChange: (leadId: string, status: Lead['status']) => void;
  onTurmaChange: (leadId: string, turmaId: string | null) => void;
  onDelete: (leadId: string) => void;
  onUpdate?: (leadId: string, data: Partial<Lead>) => void;
}

const origemConfig: Record<LeadOrigem, { label: string; icon: string; color: string; bg: string }> = {
  diagnostico: { label: 'Diagnóstico', icon: 'ri-bar-chart-2-line', color: 'text-violet-600', bg: 'bg-violet-50' },
  programa: { label: 'Interessado TR60D', icon: 'ri-graduation-cap-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

const nivelConfig: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'N1', color: 'text-red-600', bg: 'bg-red-50' },
  2: { label: 'N2', color: 'text-orange-600', bg: 'bg-orange-50' },
  3: { label: 'N3', color: 'text-amber-600', bg: 'bg-amber-50' },
  4: { label: 'N4', color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

export default function AdminLeads({ leads, turmas, onStatusChange, onTurmaChange, onDelete, onUpdate }: AdminLeadsProps) {
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<Lead['status'] | 'todos'>('todos');
  const [filtroNivel, setFiltroNivel] = useState<number | 0>(0);
  const [filtroTurma, setFiltroTurma] = useState<string>('todos');
  const [leadSelecionado, setLeadSelecionado] = useState<Lead | null>(null);
  const [leadParaExcluir, setLeadParaExcluir] = useState<Lead | null>(null);
  const [leadDiagnostico, setLeadDiagnostico] = useState<Lead | null>(null);
  const [leadProposta, setLeadProposta] = useState<Lead | null>(null);

  const leadsFiltrados = useMemo(() => {
    return leads.filter((lead) => {
      const matchBusca = busca === '' ||
        lead.nome.toLowerCase().includes(busca.toLowerCase()) ||
        lead.empresa.toLowerCase().includes(busca.toLowerCase()) ||
        lead.email.toLowerCase().includes(busca.toLowerCase());
      const matchStatus = filtroStatus === 'todos' || lead.status === filtroStatus;
      const matchNivel = filtroNivel === 0 || (lead.diagnostico?.nivelNumero === filtroNivel);
      const matchTurma = filtroTurma === 'todos' ||
        (filtroTurma === 'sem_turma' ? lead.turmaId === null : lead.turmaId === filtroTurma);
      return matchBusca && matchStatus && matchNivel && matchTurma;
    });
  }, [leads, busca, filtroStatus, filtroNivel, filtroTurma]);

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Busca */}
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm" />
            </div>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, empresa ou e-mail..."
              className="w-full bg-gray-50 border border-gray-100 rounded-xl pl-9 pr-4 py-2.5 text-gray-700 text-sm focus:outline-none focus:border-amber-400 transition-colors"
            />
          </div>

          {/* Filtro Status */}
          <CustomSelect
            options={[
              { value: 'todos', label: 'Todos os status' },
              { value: 'novo', label: 'Novo' },
              { value: 'contatado', label: 'Contatado' },
              { value: 'interessado', label: 'Interessado TR60D' },
              { value: 'confirmado', label: 'Confirmado' },
              { value: 'desistiu', label: 'Desistiu' },
            ]}
            value={filtroStatus}
            onChange={(v) => setFiltroStatus(v as Lead['status'] | 'todos')}
            placeholder="Status"
          />

          {/* Filtro Nível */}
          <CustomSelect
            options={[
              { value: '0', label: 'Todos os níveis' },
              { value: '1', label: 'Nível 1 — Em Risco' },
              { value: '2', label: 'Nível 2 — Desorganizado' },
              { value: '3', label: 'Nível 3 — Estruturando' },
              { value: '4', label: 'Nível 4 — Escalável' },
            ]}
            value={String(filtroNivel)}
            onChange={(v) => setFiltroNivel(Number(v))}
            placeholder="Nível"
          />

          {/* Filtro Turma */}
          <CustomSelect
            options={[
              { value: 'todos', label: 'Todas as turmas' },
              { value: 'sem_turma', label: 'Sem turma' },
              ...turmas.map((t) => ({ value: t.id, label: t.nome })),
            ]}
            value={filtroTurma}
            onChange={setFiltroTurma}
            placeholder="Turma"
          />
        </div>

        <p className="text-gray-400 text-xs mt-3">
          {leadsFiltrados.length} lead{leadsFiltrados.length !== 1 ? 's' : ''} encontrado{leadsFiltrados.length !== 1 ? 's' : ''}
          {leadsFiltrados.length !== leads.length && ` de ${leads.length} total`}
        </p>
      </div>

      {/* Tabela */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-5 py-4">Lead</th>
                <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-4">Contato</th>
                <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-4">Origem</th>
                <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-4">Diagnóstico</th>
                <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-4">Turma</th>
                <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-4">Status</th>
                <th className="text-left text-gray-400 text-xs font-bold uppercase tracking-wider px-4 py-4">Data</th>
                <th className="px-4 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {leadsFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400 text-sm">
                    <i className="ri-search-line text-2xl block mb-2" />
                    Nenhum lead encontrado com os filtros aplicados
                  </td>
                </tr>
              ) : (
                leadsFiltrados.map((lead) => {
                  const turma = turmas.find((t) => t.id === lead.turmaId);

                  return (
                    <tr key={lead.id} className="hover:bg-gray-50/50 transition-colors">
                      {/* Lead */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                            <span className="text-gray-600 text-xs font-black">{lead.nome.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="text-gray-900 text-sm font-bold whitespace-nowrap">{lead.nome}</p>
                            <p className="text-gray-400 text-xs">{lead.empresa}</p>
                          </div>
                        </div>
                      </td>

                      {/* Contato */}
                      <td className="px-4 py-4">
                        <p className="text-gray-600 text-xs">{lead.email}</p>
                        <p className="text-gray-400 text-xs">{lead.whatsapp}</p>
                      </td>

                      {/* Origem */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          {lead.origens.map((origem) => {
                            const cfg = origemConfig[origem];
                            return (
                              <span
                                key={origem}
                                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.bg} ${cfg.color}`}
                              >
                                <i className={`${cfg.icon} text-xs`} />
                                {cfg.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>

                      {/* Diagnóstico */}
                      <td className="px-4 py-4">
                        {lead.diagnostico ? (
                          <div>
                            <span className={`inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full ${nivelConfig[lead.diagnostico.nivelNumero].bg} ${nivelConfig[lead.diagnostico.nivelNumero].color}`}>
                              {nivelConfig[lead.diagnostico.nivelNumero].label}
                            </span>
                            <p className="text-gray-400 text-xs mt-0.5">{lead.diagnostico.percentualGeral}%</p>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Turma */}
                      <td className="px-4 py-4">
                        <div className="w-full max-w-[160px]">
                          <CustomSelect
                            options={[
                              { value: '', label: 'Sem turma' },
                              ...turmas.filter((t) => t.status !== 'encerrada').map((t) => ({ value: t.id, label: t.nome })),
                            ]}
                            value={lead.turmaId ?? ''}
                            onChange={(v) => onTurmaChange(lead.id, v || null)}
                          />
                        </div>
                        {turma && <p className="text-gray-400 text-xs mt-1 truncate max-w-[160px]">📅 {turma.nome}</p>}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4">
                        <div className="w-full min-w-[120px]">
                          <CustomSelect
                            options={[
                              { value: 'novo', label: 'Novo' },
                              { value: 'contatado', label: 'Contatado' },
                              { value: 'interessado', label: 'Interessado' },
                              { value: 'confirmado', label: 'Confirmado' },
                              { value: 'desistiu', label: 'Desistiu' },
                            ]}
                            value={lead.status}
                            onChange={(v) => onStatusChange(lead.id, v as Lead['status'])}
                          />
                        </div>
                      </td>

                      {/* Data */}
                      <td className="px-4 py-4">
                        <p className="text-gray-500 text-xs whitespace-nowrap">
                          {new Date(lead.dataCadastro).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                        </p>
                      </td>

                      {/* Ação */}
                      <td className="px-4 py-4">
                        <button
                          onClick={() => setLeadSelecionado(lead)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
                          title="Editar dados"
                        >
                          <i className="ri-file-edit-line text-amber-500 text-sm" />
                        </button>
                        {lead.diagnostico && (
                          <button
                            onClick={() => setLeadDiagnostico(lead)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                            title="Gerar diagnóstico para WhatsApp"
                          >
                            <i className="ri-file-chart-line text-gray-400 text-sm" />
                          </button>
                        )}
                        <button
                          onClick={() => setLeadProposta(lead)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
                          title="Gerar proposta comercial"
                        >
                          <i className="ri-file-text-line text-amber-500 text-sm" />
                        </button>
                        <button
                          onClick={() => setLeadParaExcluir(lead)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-red-400 text-sm" />
                        </button>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legenda */}
      <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4">
        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Legenda</p>
        <div className="flex flex-wrap gap-6">
          {/* Níveis */}
          <div>
            <p className="text-gray-500 text-xs font-bold mb-2">Diagnóstico (Nível)</p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-bold">N1 — Em Risco</span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 font-bold">N2 — Desorganizado</span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-bold">N3 — Estruturando</span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-bold">N4 — Escalável</span>
            </div>
            <p className="text-gray-400 text-xs mt-1.5 max-w-xl">
              Níveis baseados na pontuação do diagnóstico empresarial. N1 = maior gravidade, N4 = negócio estruturado.
            </p>
          </div>
          {/* Origens */}
          <div>
            <p className="text-gray-500 text-xs font-bold mb-2">Origem</p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 font-bold">
                <i className="ri-bar-chart-2-line text-xs" />Diagnóstico
              </span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-bold">
                <i className="ri-graduation-cap-line text-xs" />Interessado TR60D
              </span>
            </div>
            <p className="text-gray-400 text-xs mt-1.5 max-w-xl">
              <strong>Diagnóstico</strong> = preencheu o formulário. <strong>Interessado TR60D</strong> = clicou em "Quero participar" ou teve diagnóstico gerado pelo admin.
            </p>
          </div>
        </div>
      </div>

      {/* Modal de detalhe */}
      {leadSelecionado && (
        <LeadDetalheModal
          lead={leadSelecionado}
          turmas={turmas}
          onClose={() => setLeadSelecionado(null)}
          onStatusChange={(id, status) => {
            onStatusChange(id, status);
            setLeadSelecionado((prev) => prev ? { ...prev, status } : null);
          }}
          onTurmaChange={(id, turmaId) => {
            onTurmaChange(id, turmaId);
            setLeadSelecionado((prev) => prev ? { ...prev, turmaId } : null);
          }}
          onUpdate={onUpdate}
          defaultTab="dados"
        />
      )}

      {/* Modal de exclusão */}
      {leadParaExcluir && (
        <ConfirmDeleteModal
          lead={leadParaExcluir}
          onClose={() => setLeadParaExcluir(null)}
          onConfirm={() => {
            onDelete(leadParaExcluir.id);
            setLeadParaExcluir(null);
          }}
        />
      )}

      {/* Modal de diagnóstico WhatsApp */}
      {leadDiagnostico && (
        <GerarDiagnosticoModal
          lead={leadDiagnostico}
          onClose={() => setLeadDiagnostico(null)}
        />
      )}

      {/* Modal de proposta comercial */}
      {leadProposta && (
        <GerarPropostaModal
          lead={leadProposta}
          onClose={() => setLeadProposta(null)}
        />
      )}
    </div>
  );
}
